import React, { useState, useEffect, useMemo } from 'react';
import Modal from '@Shared.Angular/flowingly.components/Modal/Modal';
import { SelectUser } from '@Shared.Angular/flowingly.components/SelectUser/SelectUser';
import SelectTeam from '@Shared.Angular/flowingly.components/SelectTeam/SelectTeam';
import { KendoReactComboBox } from '@Shared.Angular/kendo.react/KendoReactComboBox';
import { ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';
import { getService } from '@Client/runner.hooks/useService';
import { Guid } from '@Shared.Angular/@types/guid';
import IStepReassignmentDto from '@Shared.Angular/@types/core/contracts/queryModel/delegation/stepReassignmentDto';
import { ReassignType } from '@Shared.Angular/flowingly.services/flowingly.constants';
import ActivityTaskType from '@Shared.Angular/@types/core/contracts/queryModel/workflow/steps/activityTaskType';
import IGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/group/groupDetail';
import { ActorType } from '@Client/interfaces/runner.actor-type.enum';

export interface IStepData {
  Name: string;
  StepType?: ActivityTaskType;
  StepApprovers?: IApprover[];
  ActorAssignedId?: Guid;
}

export interface ITaskData {
  Name: string;
  Id: Guid;
  StepName?: string;
}

type Props = {
  reassignType: ReassignType;
  currentStep?: IStepData;
  currentTask?: ITaskData;
  flowId: Guid;
  stepId?: Guid;
  flowIdentifier: string;
  onClose: () => void;
  onSuccess?: () => void;
};

export interface IApprover {
  ApproverUserId: Guid;
  ApproverUserName: string;
}

const FlowReassignModal = ({
  reassignType,
  currentStep,
  currentTask,
  flowId,
  stepId,
  flowIdentifier,
  onClose,
  onSuccess
}: Props) => {
  const teamApiService = getService('teamApiService');
  const flowApiService = getService('flowApiService');
  const notificationService = getService('notificationService');
  const appInsightsService = getService('appInsightsService');

  const name =
    reassignType === ReassignType.Step ? currentStep?.Name : currentTask?.Name;

  const isParallelApproverStep =
    currentStep?.StepType === ActivityTaskType.PARALLEL_APPROVAL;
  const isSequentialApproverStep =
    currentStep?.StepType === ActivityTaskType.SEQUENTIAL_APPROVAL;

  // Step tasks can only be reassigned to users, not teams
  // Steps can be reassigned to teams unless they are parallel/sequential approval steps
  const canReassignToTeam =
    reassignType === ReassignType.Step &&
    !isParallelApproverStep &&
    !isSequentialApproverStep;

  const [actorType, setActorType] = useState<ActorType>(ActorType.User);
  const [userIdToReassignTo, setUserIdToReassignTo] = useState<
    Guid | null | undefined
  >(null);
  const [teamIdToReassignTo, setTeamIdToReassignTo] = useState<
    Guid | null | undefined
  >(null);
  const [approverBeingReplaced, setApproverBeingReplaced] =
    useState<IApprover | null>(null);
  const [reason, setReason] = useState<string>('');
  const [showEmptyTeamWarning, setShowEmptyTeamWarning] =
    useState<boolean>(false);
  const [isValidatingTeam, setIsValidatingTeam] = useState<boolean>(false);
  const [approverList, setApproverList] = useState<IApprover[]>([]);
  const [reassignedFromActorId, setReassignedFromActorId] =
    useState<Guid | null>(null);
  const [excludeUserIds, setExcludeUserIds] = useState<Guid[]>([]);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (reassignType !== ReassignType.Step || !currentStep) {
      return;
    }

    if (isParallelApproverStep) {
      const approvers = currentStep.StepApprovers || [];
      const uniqueApprovers = approvers.filter(
        (approver, index, self) =>
          index ===
          self.findIndex((a) => a.ApproverUserId === approver.ApproverUserId)
      );
      setApproverList(uniqueApprovers);
      setExcludeUserIds(uniqueApprovers.map((u) => u.ApproverUserId));
    } else if (isSequentialApproverStep && currentStep.StepApprovers?.[0]) {
      setReassignedFromActorId(currentStep.StepApprovers[0].ApproverUserId);
      setExcludeUserIds([currentStep.StepApprovers[0].ApproverUserId]);
    } else if (currentStep.ActorAssignedId) {
      setReassignedFromActorId(currentStep.ActorAssignedId);
      setExcludeUserIds([currentStep.ActorAssignedId]);
    }
  }, [
    reassignType,
    currentStep,
    isParallelApproverStep,
    isSequentialApproverStep
  ]);

  useEffect(() => {
    if (actorType === ActorType.Team && teamIdToReassignTo) {
      checkTeamHasUsers(teamIdToReassignTo);
    } else {
      setShowEmptyTeamWarning(false);
    }
  }, [teamIdToReassignTo, actorType]);

  const checkTeamHasUsers = async (teamId: Guid) => {
    setIsValidatingTeam(true);
    try {
      const hasUsers = await teamApiService.checkTeamHasUsers(teamId);
      setShowEmptyTeamWarning(!hasUsers);
    } catch (error) {
      console.error('Error validating team');
    } finally {
      setIsValidatingTeam(false);
    }
  };

  const handleActorTypeChange = (newActorType: ActorType) => {
    setActorType(newActorType);
    if (newActorType === ActorType.User) {
      setShowEmptyTeamWarning(false);
    }
  };

  const reasonXssValidation = useMemo(() => {
    if (
      reason &&
      getService('validationService').isXssVulnerableString(reason)
    ) {
      return <div className="error-text">This input does not support HTML</div>;
    }
    return undefined;
  }, [reason]);

  const isFormValid = useMemo(() => {
    const hasReason = reason && reason.trim() !== '';
    const hasSelectedActor =
      actorType === ActorType.User
        ? !!userIdToReassignTo
        : !!teamIdToReassignTo;
    const hasSelectedApprover = isParallelApproverStep
      ? !!approverBeingReplaced
      : true;

    return (
      hasReason &&
      hasSelectedActor &&
      hasSelectedApprover &&
      !reasonXssValidation
    );
  }, [
    actorType,
    userIdToReassignTo,
    teamIdToReassignTo,
    approverBeingReplaced,
    reason,
    isParallelApproverStep,
    reasonXssValidation
  ]);

  const applyReassignment = async () => {
    setFormSubmitted(true);

    if (!isFormValid) {
      return;
    }

    setIsSubmitting(true);

    let finalReassignedFromId = reassignedFromActorId;

    if (isParallelApproverStep && approverBeingReplaced?.ApproverUserId) {
      finalReassignedFromId = approverBeingReplaced.ApproverUserId;
    }

    const result: IStepReassignmentDto = {
      reassignType: reassignType,
      reassignedToUserId:
        actorType === ActorType.User ? userIdToReassignTo : null,
      reassignedToGroupId:
        actorType === ActorType.Team ? teamIdToReassignTo : null,
      reassignedFromUserOrGroupId: finalReassignedFromId,
      stepTaskId:
        reassignType === ReassignType.StepTask ? currentTask?.Id : undefined,
      reason: reason,
      stepId: stepId,
      flowId: flowId
    };

    const appInsightsEventName =
      reassignType === ReassignType.Step
        ? 'stepReassigned'
        : 'stepTaskReassigned';

    appInsightsService.startEventTimer(appInsightsEventName);

    try {
      await flowApiService.reassignStep(result);

      notificationService.showSuccessToast(
        `${result.reassignType} has been successfully reassigned`
      );

      const stepName = currentStep?.Name || currentTask?.StepName;
      const taskName = currentTask?.Name;

      appInsightsService.trackMetricIfTimerExist(appInsightsEventName, {
        flowIdentifier,
        stepName,
        taskName
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      const errorMessage =
        (error as { data?: { Message?: string } })?.data?.Message ||
        `Error reassigning ${result.reassignType}`;
      notificationService.showErrorToast(errorMessage);

      const stepName = currentStep?.Name || currentTask?.StepName;
      const taskName = currentTask?.Name;

      appInsightsService.trackMetricIfTimerExist(appInsightsEventName, {
        flowIdentifier,
        stepName,
        taskName
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const showUserRequired =
    formSubmitted && actorType === ActorType.User && !userIdToReassignTo;
  const showTeamRequired =
    formSubmitted && actorType === ActorType.Team && !teamIdToReassignTo;
  const showApproverRequired =
    formSubmitted && isParallelApproverStep && !approverBeingReplaced;
  const showReasonRequired = formSubmitted && (!reason || reason.trim() === '');

  return (
    <Modal
      title={`Reassign ${reassignType}`}
      onClose={onClose}
      onCancelClick={onClose}
      actionButtonText={`Reassign ${reassignType}`}
      onActionClick={applyReassignment}
      isActionDisabled={isValidatingTeam || isSubmitting}
      size="md"
    >
      <div className="reassign-step-dialog">
        <div className="row pl-0 mr-0">
          <div className="col s6 pl-0 actor-section">
            <label>Select an actor to reassign {name} to.</label>

            {canReassignToTeam && (
              <div className="mt-8">
                <label className="row-title">Reassign to</label>
                <input
                  type="radio"
                  id="newActorTypeUser"
                  name="newActorType"
                  checked={actorType === ActorType.User}
                  onChange={() => handleActorTypeChange(ActorType.User)}
                />
                <label htmlFor="newActorTypeUser">User</label>
                <input
                  type="radio"
                  id="newActorTypeGroup"
                  name="newActorType"
                  checked={actorType === ActorType.Team}
                  onChange={() => handleActorTypeChange(ActorType.Team)}
                />
                <label htmlFor="newActorTypeGroup">Team</label>
              </div>
            )}
          </div>

          <div className="col s6 pr-0 selection-section">
            {isParallelApproverStep && (
              <>
                <label>Select Approver to reassign</label>
                <KendoReactComboBox
                  inputId="approverToReassign"
                  data={approverList}
                  textField="ApproverUserName"
                  dataItemKey="ApproverUserId"
                  placeholder="Select Approver"
                  value={approverBeingReplaced}
                  onChange={(event: ComboBoxChangeEvent) => {
                    const approver = event.value as IApprover | null;
                    setApproverBeingReplaced(approver);
                  }}
                />
                {showApproverRequired && (
                  <div className="error-text">
                    Please select an approver to reassign.
                  </div>
                )}
              </>
            )}

            {actorType === ActorType.User && (
              <>
                <label>Select User</label>
                <SelectUser
                  inputId="userReassignTo"
                  onUserSelect={setUserIdToReassignTo}
                  exclude={excludeUserIds}
                />
                {showUserRequired && (
                  <div className="error-text">
                    Please select a user to reassign this{' '}
                    {reassignType.toLowerCase()} to.
                  </div>
                )}
              </>
            )}

            {actorType === ActorType.Team && (
              <>
                <label>Select Team</label>
                <SelectTeam
                  inputId="groupReassignTo"
                  selectedTeamId={teamIdToReassignTo}
                  onTeamSelect={(team: IGroupDetail | null) =>
                    setTeamIdToReassignTo(team?.id || null)
                  }
                  placeholder="Select Team"
                />
                {showEmptyTeamWarning && (
                  <div className="reassign-empty-team-warning">
                    <i className="fa-light fa-triangle-exclamation"></i>
                    <span>
                      The selected team currently has no members. Steps assigned
                      to this team cannot be completed until users are added.
                    </span>
                  </div>
                )}
                {showTeamRequired && (
                  <div className="error-text">
                    Please select a team to reassign this{' '}
                    {reassignType.toLowerCase()} to.
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="row pl-0 mr-0 reason-section">
          <label htmlFor="reassign-modal-reason" className="row-title mt-15">
            Reason
          </label>
          <input
            id="reassign-modal-reason"
            name="reassign-modal-reason"
            type="text"
            className="text-box validate"
            autoComplete="off"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={`Add a reason for reassigning this ${reassignType.toLowerCase()}`}
          />
          {reasonXssValidation}
          {showReasonRequired && (
            <div className="error-text">Please give a reason.</div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default FlowReassignModal;
